/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { ticketColors } from '../../shared/theme/ticketColors'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal'
import { OperationalFilterChipGroup, OperationalFilterFieldLabel, OperationalFilterPickerRow } from '../../shared/filters/OperationalFilterPrimitives'
import { SALES_FILTERS_COLLAPSED_STORAGE_KEY } from '../../shared/filters/operationalFiltersCollapsedStorage';
import type { DateRangePreset } from '../infra/salesReportingConstants';
import { DATE_RANGE_PRESET_LABELS, POS_SALES_HISTORY_DATE_PRESETS } from '../infra/salesReportingConstants';
import type { BranchSalesHistoryCashierFilter } from '../application/salesListRouteParams';
import type { SaleStatusFilter, SaleFiscalStatusFilter, SaleChannelFilter, SaleCommissionFilter, SaleSortKey, SaleSortDir } from '../domain/salesTypes';

const PRESET_CHIPS: { id: DateRangePreset; label: string }[] = [
  ...POS_SALES_HISTORY_DATE_PRESETS.map((id) => ({
    id,
    label: DATE_RANGE_PRESET_LABELS[id],
  })),
  { id: 'custom', label: DATE_RANGE_PRESET_LABELS.custom },
];

const CASHIER_ALL = 'all';
const PAYMENT_METHOD_ALL = 'all';

const STATUS_OPTIONS: { id: SaleStatusFilter; name: string }[] = [
  { id: 'active', name: 'Activas' },
  { id: 'voided', name: 'Anuladas' },
];

const FISCAL_OPTIONS: { id: SaleFiscalStatusFilter; name: string }[] = [
  { id: 'fiscal', name: 'Fiscal' },
  { id: 'non_fiscal', name: 'No fiscal' },
  { id: 'pending_sync', name: 'Factura pendiente' },
];

const CHANNEL_OPTIONS: { value: SaleChannelFilter; label: string }[] = [
  { value: 'all', label: 'Todos los canales' },
  { value: 'pos', label: 'Mostrador' },
  { value: 'online', label: 'Pedido online' },
];

const COMMISSION_OPTIONS: { value: SaleCommissionFilter; label: string }[] = [
  { value: 'all', label: 'Todas' },
  { value: 'with', label: 'Con comisión' },
  { value: 'without', label: 'Sin comisión' },
];

const PENDING_OPTIONS: { id: string; name: string }[] = [
  { id: 'all', name: 'Todas las ventas' },
  { id: 'pending', name: 'Solo pendientes de cobro' },
];

type SortOption = { id: string; name: string; key: SaleSortKey; dir: SaleSortDir };

const SORT_OPTIONS: SortOption[] = [
  { id: 'created_at:desc', name: 'Más reciente primero', key: 'created_at', dir: 'desc' },
  { id: 'created_at:asc', name: 'Más vieja primero', key: 'created_at', dir: 'asc' },
  { id: 'total:desc', name: 'Mayor monto primero', key: 'total', dir: 'desc' },
  { id: 'total:asc', name: 'Menor monto primero', key: 'total', dir: 'asc' },
  { id: 'cashier_user_id:asc', name: 'Operador A-Z', key: 'cashier_user_id', dir: 'asc' },
  { id: 'cashier_user_id:desc', name: 'Operador Z-A', key: 'cashier_user_id', dir: 'desc' },
];

function sortOptionId(key: SaleSortKey, dir: SaleSortDir): string {
  return `${key}:${dir}`;
}

function sortOptionLabel(key: SaleSortKey, dir: SaleSortDir): string {
  return SORT_OPTIONS.find((o) => o.key === key && o.dir === dir)?.name ?? 'Más reciente primero';
}

export type SalesFilterPanelProps = {
  activeDatePreset: DateRangePreset | null;
  onPreset: (preset: DateRangePreset) => void;
  onOpenCustomDates: () => void;
  dateRangeLabel: string;
  cashierFilter: BranchSalesHistoryCashierFilter;
  onCashierFilterChange: (value: BranchSalesHistoryCashierFilter) => void;
  cashierOptions: Array<{ key: string; label: string }>;
  primaryStaffFilter?: BranchSalesHistoryCashierFilter;
  onPrimaryStaffFilterChange?: (value: BranchSalesHistoryCashierFilter) => void;
  primaryStaffOptions?: Array<{ key: string; label: string }>;
  statusFilter: SaleStatusFilter;
  onStatusFilterChange: (value: SaleStatusFilter) => void;
  paymentMethodFilter: string;
  onPaymentMethodFilterChange: (value: string) => void;
  paymentMethodOptions: Array<{ id: string; name: string }>;
  /** Solo ventas con cobro pendiente (fiado / parcial / QR sin pagar). */
  paymentPendingOnly?: boolean;
  onPaymentPendingOnlyChange?: (value: boolean) => void;
  fiscalStatusFilter: SaleFiscalStatusFilter;
  onFiscalStatusFilterChange: (value: SaleFiscalStatusFilter) => void;
  channelFilter?: SaleChannelFilter;
  onChannelFilterChange?: (value: SaleChannelFilter) => void;
  /** Mostrar chips de canal (tienda pública / pedidos online). */
  showChannelFilter?: boolean;
  commissionFilter?: SaleCommissionFilter;
  onCommissionFilterChange?: (value: SaleCommissionFilter) => void;
  /** Mostrar filtro de comisiones (capability + permission gated). */
  showCommissionFilter?: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  /** When true, renders only filter content (no collapsible header bar) for use inside a FilterSheetModal */
  sheetMode?: boolean;
  sortKey?: SaleSortKey;
  sortDir?: SaleSortDir;
  onSortChange?: (key: SaleSortKey, dir: SaleSortDir) => void;
};

type SheetKey = 'period' | 'cashier' | 'primaryStaff' | 'status' | 'method' | 'fiscal' | 'pending' | 'channel' | 'commission' | 'sort' | null;

export function SalesFilterPanel({
  activeDatePreset,
  onPreset,
  onOpenCustomDates,
  dateRangeLabel,
  cashierFilter,
  onCashierFilterChange,
  cashierOptions,
  primaryStaffFilter = CASHIER_ALL,
  onPrimaryStaffFilterChange,
  primaryStaffOptions = [],
  statusFilter,
  onStatusFilterChange,
  paymentMethodFilter,
  onPaymentMethodFilterChange,
  paymentMethodOptions,
  paymentPendingOnly = false,
  onPaymentPendingOnlyChange,
  fiscalStatusFilter,
  onFiscalStatusFilterChange,
  channelFilter = 'all',
  onChannelFilterChange,
  showChannelFilter = false,
  commissionFilter = 'all',
  onCommissionFilterChange,
  showCommissionFilter = false,
  hasActiveFilters,
  onClearFilters,
  sheetMode = false,
  sortKey = 'created_at',
  sortDir = 'desc',
  onSortChange,
}: SalesFilterPanelProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [openSheet, setOpenSheet] = useState<SheetKey>(null);

  useEffect(() => {
    if (sheetMode) return;
    let alive = true;
    void AsyncStorage.getItem(SALES_FILTERS_COLLAPSED_STORAGE_KEY).then((raw) => {
      if (!alive) return;
      setCollapsed(raw === '1');
    });
    return () => { alive = false; };
  }, [sheetMode]);

  const toggleCollapsed = useCallback(() => {
    const next = !collapsed;
    setCollapsed(next);
    void AsyncStorage.setItem(SALES_FILTERS_COLLAPSED_STORAGE_KEY, next ? '1' : '0');
  }, [collapsed]);

  const onPresetChange = useCallback(
    (id: DateRangePreset) => {
      onPreset(id);
      if (id === 'custom') onOpenCustomDates();
    },
    [onPreset, onOpenCustomDates]
  );

  const cashierLabel = useMemo(() => {
    if (cashierFilter === CASHIER_ALL) return 'Todos';
    const opt = cashierOptions.find((o) => o.key === cashierFilter);
    return opt?.label ?? 'Todos';
  }, [cashierFilter, cashierOptions]);

  const primaryStaffLabel = useMemo(() => {
    if (primaryStaffFilter === CASHIER_ALL) return 'Todos';
    const opt = primaryStaffOptions.find((o) => o.key === primaryStaffFilter);
    return opt?.label ?? 'Todos';
  }, [primaryStaffFilter, primaryStaffOptions]);

  const statusLabel = useMemo(() => {
    const opt = STATUS_OPTIONS.find((o) => o.id === statusFilter);
    return opt?.name ?? 'Todos';
  }, [statusFilter]);

  const methodLabel = useMemo(() => {
    if (paymentMethodFilter === PAYMENT_METHOD_ALL) return 'Todos';
    const opt = paymentMethodOptions.find((o) => o.id === paymentMethodFilter);
    return opt?.name ?? 'Todos';
  }, [paymentMethodFilter, paymentMethodOptions]);

  const fiscalLabel = useMemo(() => {
    const opt = FISCAL_OPTIONS.find((o) => o.id === fiscalStatusFilter);
    return opt?.name ?? 'Todos';
  }, [fiscalStatusFilter]);

  const cashierSheetOptions = useMemo(
    () => cashierOptions.map((opt) => ({ id: opt.key, name: opt.label })),
    [cashierOptions]
  );

  const primaryStaffSheetOptions = useMemo(
    () => primaryStaffOptions.map((opt) => ({ id: opt.key, name: opt.label })),
    [primaryStaffOptions]
  );

  const methodSheetOptions = useMemo(
    () => paymentMethodOptions.map((opt) => ({ id: opt.id, name: opt.name })),
    [paymentMethodOptions]
  );

  const showPrimaryStaffFilter =
    typeof onPrimaryStaffFilterChange === 'function' && primaryStaffOptions.length > 0;

  const activeCount = useMemo(() => {
    let count = 0;
    if (activeDatePreset !== 'today') count++;
    if (cashierFilter !== CASHIER_ALL) count++;
    if (showPrimaryStaffFilter && primaryStaffFilter !== CASHIER_ALL) count++;
    if (statusFilter !== 'active') count++;
    if (paymentMethodFilter !== PAYMENT_METHOD_ALL) count++;
    if (fiscalStatusFilter !== 'all') count++;
    if (showChannelFilter && channelFilter !== 'all') count++;
    if (showCommissionFilter && commissionFilter !== 'all') count++;
    if (paymentPendingOnly) count++;
    return count;
  }, [activeDatePreset, cashierFilter, showPrimaryStaffFilter, primaryStaffFilter, statusFilter, paymentMethodFilter, fiscalStatusFilter, showChannelFilter, channelFilter, showCommissionFilter, commissionFilter, paymentPendingOnly]);

  type FilterCell = {
    key: SheetKey;
    icon: keyof typeof Ionicons.glyphMap;
    label: string;
    valueLabel: string;
    active: boolean;
    onPress: () => void;
    accessibilityLabel: string;
  };

  const filterCells: FilterCell[] = [
    {
      key: 'cashier',
      icon: 'person-outline',
      label: 'Operador',
      valueLabel: cashierLabel,
      active: cashierFilter !== CASHIER_ALL,
      onPress: () => setOpenSheet('cashier'),
      accessibilityLabel: `Filtrar por operador, actual: ${cashierLabel}`,
    },
    ...(showPrimaryStaffFilter
      ? [{
          key: 'primaryStaff' as SheetKey,
          icon: 'people-outline' as keyof typeof Ionicons.glyphMap,
          label: 'Atendió',
          valueLabel: primaryStaffLabel,
          active: primaryStaffFilter !== CASHIER_ALL,
          onPress: () => setOpenSheet('primaryStaff'),
          accessibilityLabel: `Filtrar por quién atendió, actual: ${primaryStaffLabel}`,
        }]
      : []),
    {
      key: 'status',
      icon: 'checkmark-circle-outline',
      label: 'Estado',
      valueLabel: statusLabel,
      active: statusFilter !== 'active',
      onPress: () => setOpenSheet('status'),
      accessibilityLabel: `Filtrar por estado, actual: ${statusLabel}`,
    },
    ...(paymentMethodOptions.length > 0
      ? [{
          key: 'method' as SheetKey,
          icon: 'card-outline' as keyof typeof Ionicons.glyphMap,
          label: 'Medio de cobro',
          valueLabel: methodLabel,
          active: paymentMethodFilter !== PAYMENT_METHOD_ALL,
          onPress: () => setOpenSheet('method'),
          accessibilityLabel: `Filtrar por medio de cobro, actual: ${methodLabel}`,
        }]
      : []),
    {
      key: 'fiscal',
      icon: 'document-text-outline',
      label: 'Estado fiscal',
      valueLabel: fiscalLabel,
      active: fiscalStatusFilter !== 'all',
      onPress: () => setOpenSheet('fiscal'),
      accessibilityLabel: `Filtrar por estado fiscal, actual: ${fiscalLabel}`,
    },
    ...(typeof onPaymentPendingOnlyChange === 'function'
      ? [{
          key: 'pending' as SheetKey,
          icon: 'time-outline' as keyof typeof Ionicons.glyphMap,
          label: 'Cobro',
          valueLabel: paymentPendingOnly ? 'Solo pendientes' : 'Todas',
          active: paymentPendingOnly,
          onPress: () => setOpenSheet('pending'),
          accessibilityLabel: `Filtrar por cobro pendiente, actual: ${paymentPendingOnly ? 'Solo pendientes' : 'Todas'}`,
        }]
      : []),
  ];

  const renderFilterCell = (cell: FilterCell) => (
    <TouchableOpacity
      key={cell.key}
      style={[styles.filterCell, cell.active && styles.filterCellActive]}
      onPress={cell.onPress}
      activeOpacity={0.55}
      accessibilityRole="button"
      accessibilityLabel={cell.accessibilityLabel}
    >
      <View style={styles.filterCellTop}>
        <Ionicons
          name={cell.icon}
          size={16}
          color={cell.active ? ticketColors.gold : ticketColors.ink3}
        />
        <Text style={styles.filterCellLabel}>{cell.label}</Text>
      </View>
      <Text
        style={[styles.filterCellValue, cell.active && styles.filterCellValueActive]}
        numberOfLines={1}
      >
        {cell.valueLabel}
      </Text>
    </TouchableOpacity>
  );

  const filterBody = (
    <>
      {/* Period card — prominent, full-width */}
      <TouchableOpacity
        style={styles.periodCard}
        onPress={() => onOpenCustomDates()}
        activeOpacity={0.55}
        accessibilityRole="button"
        accessibilityLabel={`Período: ${dateRangeLabel}`}
      >
        <View style={styles.periodCardLeft}>
          <View style={styles.periodIconWrap}>
            <Ionicons name="calendar" size={20} color={ticketColors.gold} />
          </View>
          <View style={styles.periodTextCol}>
            <Text style={styles.periodLabel}>Período</Text>
            <Text style={styles.periodValue} numberOfLines={1}>
              {dateRangeLabel}
            </Text>
          </View>
        </View>
        <Ionicons name="chevron-forward" size={18} color={ticketColors.ink3} />
      </TouchableOpacity>

      {/* Preset chips — wrapping grid */}
      <View style={styles.presetWrap}>
        {PRESET_CHIPS.map((chip) => {
          const isActive = activeDatePreset === chip.id;
          return (
            <TouchableOpacity
              key={chip.id}
              style={[styles.presetPill, isActive && styles.presetPillActive]}
              onPress={() => onPresetChange(chip.id)}
              activeOpacity={0.55}
              accessibilityRole="button"
              accessibilityLabel={`Período: ${chip.label}`}
              accessibilityState={{ selected: isActive }}
            >
              <Text style={[styles.presetPillText, isActive && styles.presetPillTextActive]}>
                {chip.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {showChannelFilter && typeof onChannelFilterChange === 'function' ? (
        <View style={styles.channelBlock}>
          <OperationalFilterFieldLabel>Canal de venta</OperationalFilterFieldLabel>
          <OperationalFilterChipGroup
            options={CHANNEL_OPTIONS}
            value={channelFilter}
            onChange={onChannelFilterChange}
            ticketTone={sheetMode}
          />
        </View>
      ) : null}

      {showCommissionFilter && typeof onCommissionFilterChange === 'function' ? (
        <View style={styles.channelBlock}>
          <OperationalFilterFieldLabel>Comisiones</OperationalFilterFieldLabel>
          <OperationalFilterChipGroup
            options={COMMISSION_OPTIONS}
            value={commissionFilter}
            onChange={onCommissionFilterChange}
            ticketTone={sheetMode}
          />
        </View>
      ) : null}

      {/* Filter cells — 2-column grid */}
      <View style={styles.filterGrid}>
        {filterCells.map(renderFilterCell)}
      </View>
    </>
  );

  const bottomSheets = (
    <>
      <MasterFilterSheetModal
        visible={openSheet === 'cashier'}
        title="Operador"
        options={cashierSheetOptions}
        selectedId={cashierFilter === CASHIER_ALL ? null : cashierFilter}
        allOptionLabel="Todos"
        appearance={sheetMode ? 'ticket' : 'default'}
        onSelect={(id: string | null) =>
          onCashierFilterChange((id ?? CASHIER_ALL) as BranchSalesHistoryCashierFilter)
        }
        onClose={() => setOpenSheet(null)}
      />
      {showPrimaryStaffFilter ? (
        <MasterFilterSheetModal
          visible={openSheet === 'primaryStaff'}
          title="Atendió"
          options={primaryStaffSheetOptions}
          selectedId={primaryStaffFilter === CASHIER_ALL ? null : primaryStaffFilter}
          allOptionLabel="Todos"
          appearance={sheetMode ? 'ticket' : 'default'}
          onSelect={(id: string | null) =>
            onPrimaryStaffFilterChange?.((id ?? CASHIER_ALL) as BranchSalesHistoryCashierFilter)
          }
          onClose={() => setOpenSheet(null)}
        />
      ) : null}
      <MasterFilterSheetModal
        visible={openSheet === 'status'}
        title="Estado"
        options={STATUS_OPTIONS}
        selectedId={statusFilter === 'all' ? null : statusFilter}
        allOptionLabel="Todos"
        appearance={sheetMode ? 'ticket' : 'default'}
        onSelect={(id: string | null) =>
          onStatusFilterChange((id as SaleStatusFilter) ?? 'all')
        }
        onClose={() => setOpenSheet(null)}
      />
      {paymentMethodOptions.length > 0 ? (
        <MasterFilterSheetModal
          visible={openSheet === 'method'}
          title="Medio de cobro"
          options={methodSheetOptions}
          selectedId={paymentMethodFilter === PAYMENT_METHOD_ALL ? null : paymentMethodFilter}
          allOptionLabel="Todos"
          appearance={sheetMode ? 'ticket' : 'default'}
          onSelect={(id: string | null) =>
            onPaymentMethodFilterChange(id ?? PAYMENT_METHOD_ALL)
          }
          onClose={() => setOpenSheet(null)}
        />
      ) : null}
      <MasterFilterSheetModal
        visible={openSheet === 'fiscal'}
        title="Estado fiscal"
        options={FISCAL_OPTIONS}
        selectedId={fiscalStatusFilter === 'all' ? null : fiscalStatusFilter}
        allOptionLabel="Todos"
        appearance={sheetMode ? 'ticket' : 'default'}
        onSelect={(id: string | null) =>
          onFiscalStatusFilterChange((id as SaleFiscalStatusFilter) ?? 'all')
        }
        onClose={() => setOpenSheet(null)}
      />
      {typeof onPaymentPendingOnlyChange === 'function' ? (
        <MasterFilterSheetModal
          visible={openSheet === 'pending'}
          title="Cobro"
          options={PENDING_OPTIONS}
          selectedId={paymentPendingOnly ? 'pending' : 'all'}
          showAllOption={false}
          appearance={sheetMode ? 'ticket' : 'default'}
          onSelect={(id: string | null) => onPaymentPendingOnlyChange(id === 'pending')}
          onClose={() => setOpenSheet(null)}
        />
      ) : null}
      {showChannelFilter && typeof onChannelFilterChange === 'function' ? (
        <MasterFilterSheetModal
          visible={openSheet === 'channel'}
          title="Canal de venta"
          options={CHANNEL_OPTIONS.map((o) => ({ id: o.value, name: o.label }))}
          selectedId={channelFilter === 'all' ? null : channelFilter}
          allOptionLabel="Todos los canales"
          appearance={sheetMode ? 'ticket' : 'default'}
          onSelect={(id: string | null) =>
            onChannelFilterChange((id as SaleChannelFilter) ?? 'all')
          }
          onClose={() => setOpenSheet(null)}
        />
      ) : null}
      {showCommissionFilter && typeof onCommissionFilterChange === 'function' ? (
        <MasterFilterSheetModal
          visible={openSheet === 'commission'}
          title="Comisiones"
          options={COMMISSION_OPTIONS.map((o) => ({ id: o.value, name: o.label }))}
          selectedId={commissionFilter === 'all' ? null : commissionFilter}
          allOptionLabel="Todas"
          appearance={sheetMode ? 'ticket' : 'default'}
          onSelect={(id: string | null) =>
            onCommissionFilterChange((id as SaleCommissionFilter) ?? 'all')
          }
          onClose={() => setOpenSheet(null)}
        />
      ) : null}
      {onSortChange ? (
        <MasterFilterSheetModal
          visible={openSheet === 'sort'}
          title="Ordenar por"
          options={SORT_OPTIONS}
          selectedId={sortOptionId(sortKey, sortDir)}
          appearance={sheetMode ? 'ticket' : 'default'}
          onSelect={(id: string | null) => {
            if (!id) return;
            const opt = SORT_OPTIONS.find((o) => o.id === id);
            if (opt) onSortChange(opt.key, opt.dir);
          }}
          onClose={() => setOpenSheet(null)}
        />
      ) : null}
    </>
  );

  if (sheetMode) {
    return (
      <View style={styles.sheetBody}>
        <View style={styles.sheetSection}>
          <OperationalFilterFieldLabel>Período</OperationalFilterFieldLabel>
          <OperationalFilterPickerRow
            layout="stack"
            label="Rango"
            valueLabel={dateRangeLabel}
            active
            onPress={onOpenCustomDates}
            accessibilityLabel={`Período: ${dateRangeLabel}`}
          />
          <OperationalFilterChipGroup
            ticketTone
            options={PRESET_CHIPS.map((c) => ({ value: c.id, label: c.label }))}
            value={activeDatePreset ?? 'custom'}
            onChange={onPresetChange}
          />
        </View>
        <View style={styles.sheetSection}>
          <OperationalFilterFieldLabel>Detalle</OperationalFilterFieldLabel>
          {filterCells.map((cell) => (
            <OperationalFilterPickerRow
              key={cell.key}
              layout="stack"
              label={cell.label}
              valueLabel={cell.valueLabel}
              active={cell.active}
              onPress={cell.onPress}
              accessibilityLabel={cell.accessibilityLabel}
            />
          ))}
        </View>
        {onSortChange ? (
          <View style={styles.sheetSection}>
            <OperationalFilterFieldLabel>Orden</OperationalFilterFieldLabel>
            <OperationalFilterPickerRow
              layout="stack"
              label="Ordenar por"
              valueLabel={sortOptionLabel(sortKey, sortDir)}
              active={sortOptionId(sortKey, sortDir) !== 'created_at:desc'}
              onPress={() => setOpenSheet('sort')}
              accessibilityLabel={`Ordenar por, actual: ${sortOptionLabel(sortKey, sortDir)}`}
            />
          </View>
        ) : null}
        {bottomSheets}
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Compact header bar */}
      <TouchableOpacity
        style={styles.headerBar}
        onPress={toggleCollapsed}
        activeOpacity={0.6}
        accessibilityRole="button"
        accessibilityLabel={collapsed ? 'Expandir filtros' : 'Colapsar filtros'}
        accessibilityState={{ expanded: !collapsed }}
      >
        <View style={styles.headerLeft}>
          <Ionicons name="filter" size={16} color={ticketColors.ink3} />
          <Text style={styles.headerTitle}>Filtros</Text>
          {activeCount > 0 ? (
            <View style={styles.activeBadge}>
              <Text style={styles.activeBadgeText}>{activeCount}</Text>
            </View>
          ) : null}
        </View>
        <View style={styles.headerRight}>
          {hasActiveFilters ? (
            <Pressable
              hitSlop={8}
              onPress={(e) => { e.stopPropagation(); onClearFilters(); }}
              accessibilityRole="button"
              accessibilityLabel="Limpiar filtros"
            >
              <Text style={styles.clearLinkText}>Limpiar filtros</Text>
            </Pressable>
          ) : null}
          <Ionicons
            name={collapsed ? 'chevron-down' : 'chevron-up'}
            size={18}
            color={ticketColors.ink3}
          />
        </View>
      </TouchableOpacity>

      {collapsed ? null : (
        <View style={styles.body}>
          {filterBody}
        </View>
      )}

      {bottomSheets}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    ...ticketSurfaces.paperSheet,
    marginBottom: spacing.sm,
  },
  // ── Header bar ──
  headerBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: 10,
    minHeight: 44,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  headerTitle: {
    ...typography.body,
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  activeBadge: {
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 5,
  },
  activeBadgeText: {
    fontSize: 10,
    fontWeight: '800',
    color: '#FFFFFF',
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  clearLinkText: {
    ...typography.caption,
    fontSize: 13,
    color: ticketColors.gold,
    fontWeight: '700',
  },
  // ── Body ──
  body: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
    gap: spacing.sm + 2,
  },
  sheetBody: {
    gap: 18,
  },
  sheetSection: {
    gap: 8,
  },
  // ── Period card ──
  periodCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    backgroundColor: ticketColors.brandSoft,
    borderWidth: 1,
    borderColor: ticketColors.brandSoft,
    minHeight: 64,
  },
  periodCardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 0,
  },
  periodIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: ticketColors.brandSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  periodTextCol: {
    flex: 1,
    minWidth: 0,
    gap: 1,
  },
  periodLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  periodValue: {
    ...typography.body,
    fontSize: 16,
    fontWeight: '800',
    color: ticketColors.ink,
  },
  // ── Preset pills ──
  presetWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  channelBlock: {
    gap: 8,
  },
  presetPill: {
    paddingVertical: 7,
    paddingHorizontal: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    minHeight: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  presetPillActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
  },
  presetPillText: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ink2,
  },
  presetPillTextActive: {
    color: '#FFFFFF',
  },
  // ── Filter grid ──
  filterGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  filterCell: {
    flexBasis: '47%',
    flexGrow: 1,
    gap: 6,
    paddingVertical: 12,
    paddingHorizontal: spacing.sm + 2,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    minHeight: 64,
  },
  filterCellActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  filterCellTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    flexWrap: 'wrap',
  },
  filterCellLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    flexShrink: 1,
  },
  filterCellValue: {
    ...typography.body,
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  filterCellValueActive: {
    color: ticketColors.gold,
  },
});
